import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, Chip, Container, FormControlLabel, Grid, IconButton, Stack, SwipeableDrawer, Switch, TextField, Tooltip, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { dispatch, useSelector } from '../../../../redux/store';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import useSettings from '../../../../hooks/useSettings';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../components/table/DataGridTable';
import Iconify from '../../../../components/Iconify';
import PermissionRequired from '../../../errorPages/permissionRequired';
import { accessVerify, capitalize } from '../../../../utils/common';
import {
  addUtilityCategory,
  deactivateUtilityCategory,
  getUtilityCategories,
  updateUtilityCategory,
} from '../../../../redux/slices/utilityRedux/utilityCategoriesRedux';

const defaultDataModel = {
  id: '',
  name: '',
  description: '',
  status: 1,
};

export default function UtilityCategories() {
  const { themeStretch } = useSettings();
  const [selectedDataObj, setSelectedDataObj] = useState(defaultDataModel);
  const [isAdd, setIsAdd] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState(false);
  const [showInactiveCategories, setShowInactiveCategories] = useState(false);

  const { data, addData } = useSelector((state) => state.utilityCategories);

  const displayedCategories = useMemo(() => {
    const categories = data || [];

    if (showInactiveCategories) {
      return categories;
    }

    return categories.filter((category) => Number(category?.status) === 1);
  }, [data, showInactiveCategories]);

  useEffect(() => {
    dispatch(getUtilityCategories());
  }, []);

  useEffect(() => {
    if (addData?.data) {
      dispatch(getUtilityCategories());
      setIsModelOpen(false);
      setSelectedDataObj(defaultDataModel);
    }
  }, [addData]);

  const manageModel = (modelData = defaultDataModel, type = 'add') => {
    if (isModelOpen) {
      setIsModelOpen(false);
      setSelectedDataObj(defaultDataModel);
    } else {
      setIsModelOpen(true);
      setIsAdd(type === 'add');
      setSelectedDataObj(type === 'add' ? defaultDataModel : modelData);
    }
  };

  const updateEditingData = (key, value) => {
    setSelectedDataObj((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = () => {
    if (!selectedDataObj.name?.trim()) {
      toast.error('Utility category name is required!');
      return;
    }

    const payload = {
      ...selectedDataObj,
      name: selectedDataObj.name.trim(),
      description: selectedDataObj.description || '',
      status: Number(selectedDataObj.status) === 0 ? 0 : 1,
    };

    dispatch(isAdd ? addUtilityCategory(payload) : updateUtilityCategory(payload));
  };

  const handleStatusChange = (row, nextStatus, event) => {
    event?.stopPropagation();

    const isActivation = Number(nextStatus) === 1;
    const requiredPermission = isActivation ? 'UTILITY_CATEGORY_UPDATE' : 'UTILITY_CATEGORY_DELETE';

    if (!accessVerify(requiredPermission)) {
      toast.error(`You do not have permission to ${isActivation ? 'activate' : 'deactivate'} utility categories.`);
      return;
    }

    if (window.confirm(`Are you sure you want to ${isActivation ? 'activate' : 'deactivate'} ${row?.original?.name}?`)) {
      if (isActivation) {
        dispatch(updateUtilityCategory({ ...row?.original, status: 1 }));
      } else {
        dispatch(deactivateUtilityCategory({ id: row?.original?.id }));
      }
    }
  };

  const columns = [
    {
      accessorKey: 'id',
      header: 'ID',
    },
    {
      accessorKey: 'name',
      header: 'Name',
      Cell: ({ row }) => capitalize(row?.original?.name || ''),
    },
    {
      accessorKey: 'description',
      header: 'Description',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      Cell: ({ row }) =>
        Number(row?.original?.status) === 1 ? (
          <Chip size='small' label='Active' color='success' sx={{ width: 90 }} />
        ) : (
          <Chip size='small' label='Inactive' color='default' sx={{ width: 90 }} />
        ),
    },
  ];

  return (
    <Page title='Utility Categories'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading='Utility Categories'
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Administration', href: PATH_DASHBOARD.administration.root },
            { name: 'Utilities' },
            { name: 'Utility Categories' },
          ]}
          action={
            <Stack direction='row' spacing={2} alignItems='center'>
              <FormControlLabel
                control={<Switch checked={showInactiveCategories} onChange={(event) => setShowInactiveCategories(event.target.checked)} />}
                label='Show In-active Categories'
              />
              {accessVerify('UTILITY_CATEGORY_ADD') ? (
                <Button variant='contained' startIcon={<Iconify icon='eva:plus-fill' />} onClick={() => manageModel(defaultDataModel, 'add')}>
                  New Utility Category
                </Button>
              ) : null}
            </Stack>
          }
        />

        {accessVerify('UTILITY_CATEGORY_VIEW') ? (
          <DataGridTable
            name='Utility Category List'
            data={displayedCategories}
            column={columns}
            isLoading={false}
            onRowClick={(row) => {
              if (accessVerify('UTILITY_CATEGORY_UPDATE')) {
                manageModel(row?.original, 'edit');
              }
            }}
            isRowClickable={accessVerify('UTILITY_CATEGORY_UPDATE')}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            enableRowSelection={false}
            enableRowActions={accessVerify('UTILITY_CATEGORY_UPDATE') || accessVerify('UTILITY_CATEGORY_DELETE')}
            renderRowActionItems={(row) => {
              const isActive = Number(row?.original?.status) === 1;

              if (isActive) {
                return [
                  <Tooltip title='Deactivate' key='deactivate'>
                    <span>
                      <IconButton color='error' disabled={!accessVerify('UTILITY_CATEGORY_DELETE')} onClick={(event) => handleStatusChange(row, 0, event)}>
                        <Iconify icon='solar:trash-bin-minimalistic-outline' width={20} height={20} />
                      </IconButton>
                    </span>
                  </Tooltip>,
                ];
              }

              return [
                <Tooltip title='Activate' key='activate'>
                  <span>
                    <IconButton color='success' disabled={!accessVerify('UTILITY_CATEGORY_UPDATE')} onClick={(event) => handleStatusChange(row, 1, event)}>
                      <Iconify icon='eva:checkmark-circle-2-fill' width={20} height={20} />
                    </IconButton>
                  </span>
                </Tooltip>,
              ];
            }}
          />
        ) : (
          <PermissionRequired />
        )}

        <SwipeableDrawer
          anchor='top'
          open={isModelOpen}
          onOpen={() => setIsModelOpen(true)}
          onClose={() => manageModel()}
          PaperProps={{
            sx: {
              width: '90%',
              height: '90%',
              marginLeft: '5%',
              marginTop: '3%',
              borderRadius: '10px',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <h1>{isAdd ? 'Add' : 'Edit'} Utility Category</h1>
          </Box>
          <Grid container spacing={1}>
            <Grid item xs={12} md={8}>
              <Stack spacing={3} sx={{ p: 3 }}>
                <Card sx={{ p: 3 }}>
                  <Typography variant='subtitle1' padding='10px 0'>
                    Utility Category Information
                  </Typography>
                  <Stack spacing={3}>
                    <TextField fullWidth label='Name' value={selectedDataObj?.name || ''} onChange={(e) => updateEditingData('name', e.target.value)} />
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label='Description'
                      value={selectedDataObj?.description || ''}
                      onChange={(e) => updateEditingData('description', e.target.value)}
                    />
                  </Stack>
                </Card>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3 }}>
                <Stack spacing={3} sx={{ p: 3 }}>
                  <LoadingButton type='submit' variant='contained' size='large' style={{ width: '100%' }} onClick={handleSubmit}>
                    {isAdd ? 'Add Utility Category' : 'Save Changes'}
                  </LoadingButton>
                  <Button color='info' variant='outlined' size='large' style={{ width: '100%' }} onClick={() => manageModel()}>
                    Close
                  </Button>
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </SwipeableDrawer>
      </Container>
    </Page>
  );
}
